import { useTranslation } from "react-i18next";
import {
  NumberField,
  SettingRow,
  SettingSwitch,
  SettingsCard,
} from "@/features/changelog/components/shared/SettingsCard";
import { ModelPriceEditor } from "@/features/llm-usage/components/ModelPriceEditor";
import { centsToUsd, usdToCents } from "@/features/llm-usage/lib/money";
import { BUDGET_DEFAULT_CENTS, LIMITS } from "./ops-settings-limits";
import { SlackWarning } from "./SlackWarning";
import type { OpsSettingsCardProps } from "./types";

/**
 * The rates that turn token counts into money, and the monthly figure that
 * spend is measured against. Unlike every other card here the price table saves
 * on its own row-by-row, so it is not part of the draft the footer tracks.
 */
export function CostSettingsCard({
  draft,
  set,
  isReadOnly,
  isSlackConfigured,
}: OpsSettingsCardProps) {
  const { t } = useTranslation();
  const budgetOn = draft.monthlyBudgetUsdCents > 0;

  return (
    <SettingsCard
      icon="target"
      title={t("admin.opsSettings.cost.title", "Cost and budget")}
      description={t(
        "admin.opsSettings.cost.description",
        "What each model charges, which is what turns the token counts on the tab into money — and the monthly figure the spend is measured against.",
      )}
    >
      <SettingRow
        htmlFor="monthly-budget"
        label={t("admin.opsSettings.budget", "Set a monthly budget")}
        description={t(
          "admin.opsSettings.budgetHelp",
          "Alerting only — nothing is blocked when the budget is reached. A quiet month of steady overspend trips this and nothing else.",
        )}
        control={
          <SettingSwitch
            id="monthly-budget"
            checked={budgetOn}
            disabled={isReadOnly}
            onCheckedChange={(checked) =>
              set("monthlyBudgetUsdCents", checked ? BUDGET_DEFAULT_CENTS : 0)
            }
          />
        }
      />

      <SettingRow
        htmlFor="monthly-budget-usd"
        isNested
        isMuted={!budgetOn}
        label={t("admin.opsSettings.budgetAmount", "Budget for the month")}
        description={t(
          "admin.opsSettings.budgetAmountHelp",
          "Measured against spend since the first of the month, UTC, using the rates below. Models with no rate set are not counted.",
        )}
        control={
          <NumberField
            id="monthly-budget-usd"
            value={Math.round(
              centsToUsd(budgetOn ? draft.monthlyBudgetUsdCents : BUDGET_DEFAULT_CENTS),
            )}
            min={LIMITS.monthlyBudgetUsd.min}
            max={LIMITS.monthlyBudgetUsd.max}
            unit={t("admin.opsSettings.usd", "USD")}
            disabled={isReadOnly || !budgetOn}
            onChange={(value) => set("monthlyBudgetUsdCents", usdToCents(value))}
          />
        }
      />

      <SettingRow
        htmlFor="budget-warn-percent"
        isNested
        isMuted={!budgetOn}
        label={t("admin.opsSettings.budgetWarnAt", "Warn at")}
        description={t(
          "admin.opsSettings.budgetWarnAtHelp",
          "The early warning, once a month. Passing the budget itself always alerts as well, so a budget is never crossed silently.",
        )}
        control={
          <NumberField
            id="budget-warn-percent"
            value={draft.budgetAlertAtPercent}
            min={LIMITS.budgetAlertAtPercent.min}
            max={LIMITS.budgetAlertAtPercent.max}
            unit={t("admin.opsSettings.percentOfBudget", "% of budget")}
            disabled={isReadOnly || !budgetOn}
            onChange={(value) => set("budgetAlertAtPercent", value)}
          />
        }
      />

      {budgetOn && !isSlackConfigured ? <SlackWarning /> : null}

      <div>
        <div className="px-5 pt-4">
          <h3 className="m-0 text-xs font-medium text-foreground">
            {t("admin.opsSettings.prices.title", "Model prices")}
          </h3>
          <p className="m-0 mt-0.5 max-w-[62ch] text-xs leading-relaxed text-muted-foreground">
            {t(
              "admin.opsSettings.prices.description",
              "Models with a published list price are priced automatically, and an admin's edit is never overwritten. A model with no list price is counted in tokens but not in cost until someone sets its rate.",
            )}
          </p>
        </div>
        <ModelPriceEditor isReadOnly={isReadOnly} />
      </div>
    </SettingsCard>
  );
}
